import { Component, HostListener, Inject, OnInit, OnDestroy, Optional, ViewChild, ElementRef, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { RequestsService } from '../shared/services/requests.service';
import { SnackbarService } from '../shared/services/snackbar.service';
import { TransferFundResponseDto } from '../shared/models/transferFundResponseDto';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { RequestWorkflowDto } from '../shared/models/request-stage';
import { ReturnReasonDialogComponent } from '../return-reason-dialog/return-reason-dialog.component';
import { RequestType, RequestTypeOrder } from '../enums/request-type.enum';
import { DepositAccountService, DepositAccount } from '../shared/services/deposit-account.service';
import { AuditService } from '../shared/services/audit.service';
import { Bill } from '../shared/models/bill';
import { BillService } from '../shared/services/billService.service';
import { StagesLabel } from '../enums/stages.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { ManageUserRoleService } from '../shared/services/manage-user-role.service';
import { UserRole } from '../shared/models/user-role';
import { BankAccountService } from '../shared/services/bank-account.service';
import { BankAccount } from '../shared/models/bankAccount';
import { RpaStatus } from '../enums/rpa-status.enum';
import { Subject, takeUntil } from 'rxjs';
import { applyGeneratedTransactionReference, buildTransferTransactionReference } from '../shared/utils/transaction-reference';
import { ApprovalDetails } from '../shared/models/ApprovalDetails';
import { buildTransferReason } from '../shared/utils/transfer-reason';

const STAGE_PARTNER = 3;
const STAGE_ACCOUNTS = 4;
const STAGE_COMPLETED = 5;

@Component({
    selector: 'xp-view-transfer-fund-request',
    templateUrl: './view-transfer-fund-request.component.html',
    styleUrl: './view-transfer-fund-request.component.scss'
})
export class ViewTransferFundRequestComponent implements OnInit, OnDestroy {
    @ViewChild('dialogContent', { read: ElementRef }) dialogContent!: ElementRef;

    private readonly destroy$ = new Subject<void>();
    private readonly boundStorageHandler = this.onStorageEvent.bind(this);

    currentDate: Date = new Date(Date.now());
    RequestType = RequestType;
    transferFundRequestForm!: FormGroup;
    transferFundData!: TransferFundResponseDto;
    disableButtons = false;
    invoices: [string, string][] = [];
    bills: Bill[] = [];
    invoiceValues: { [key: string]: number } = {};
    invoiceMap: { [key: string]: { totalAmount: number; balance: number } } = {};
    noBillsFoundMessage: string | null = null;
    displayedColumns: string[] = ['pay', 'warning', 'amount'];
    currencySymbol: string | undefined;
    approveBtn = 'Accept';
    previousStages: RequestWorkflowDto[] = [];
    accountsStage = false;
    canEditFields = false;
    private reasonEditedByUser = false;
    showReturnReason = false;
    paymentMethodDropdown: string[] = ['Electronic funds transfer'];
    showAccountFields = false;
    depositAccounts: DepositAccount[] = [];
    isAmountReleased = false;
    isAmountReleasedDisabled = false;
    showFundsCheckedByPartner: boolean = false;
    FundsCheckedPartnerText: boolean = false;
    FundsCheckedAccountsText: boolean = false;
    isLastAuditRpaSuccessful = false;
    actionstepBaseUrl = '';
    actionstepMatterUrl = '';
    showLinkToOpenMatter: WritableSignal<boolean> = signal(false);
    matterNo = '';
    StagesLabel = StagesLabel;
    isDialogOpen: WritableSignal<boolean> = signal(false);
    isTransferFundsLoading: WritableSignal<boolean> = signal(true);
    filteredBankAccounts: BankAccount[] = [];
    actionstepMatterOverviewUrl = '';
    actionstepOverviewBaseUrl = '';
    bankAccounts: BankAccount[] = [];
    selectedBankAccountId: string = '';
    /** The reference this screen last generated, so a reference the Accounts user typed is never overwritten. */
    private lastGeneratedTransactionReference = '';

    constructor(@Optional() @Inject(MAT_DIALOG_DATA) public data: TransferFundResponseDto,
        private formBuilder: FormBuilder,
        private dialog: MatDialog, private requestService: RequestsService,
        private snackbarService: SnackbarService,
        private depositAccountService: DepositAccountService,
        private auditService: AuditService,
        @Optional() public dialogRef: MatDialogRef<ViewTransferFundRequestComponent>,
        private router: Router,
        private route: ActivatedRoute,
        private billService: BillService,
        private manageUserRoleService: ManageUserRoleService,
        private bankAccountService: BankAccountService) {
        this.isDialogOpen.set(!!this.data);
        this.transferFundRequestForm = this.formBuilder.group({
            returnReason: [''],
            matterNumber: '',
            client: '',
            matter: '',
            matterName: '',
            reason: '',
            invoice: '',
            amount: '',
            dateTransaction: [new Date(), Validators.required],
            dateAuthorized: '',
            dateRequested: '',
            requestedTime: [{ value: '', disabled: true }],
            transactionTime: [{ value: '' }], paymentMethod: [''],
            depositAccount: [''],
            transactionReference: [''],
            isCheckedLedger: false,
            fundsCheckedByPartner: [false, Validators.requiredTrue],
            comments: '',
            clientBankAccount: ['', Validators.required],
            partnerAuthorising: '',
        })

        this.transferFundRequestForm.get('matterName')?.disable();
        this.transferFundRequestForm.get('client')?.disable();
        this.transferFundRequestForm.get('reason')?.disable();
        this.transferFundRequestForm.get('amount')?.disable();
        this.transferFundRequestForm.get('dateAuthorized')?.disable();
        this.transferFundRequestForm.get('requestDate')?.disable();
        this.transferFundRequestForm.get('partnerAuthorising')?.disable();

        if (this.dialogRef) {
            dialogRef.disableClose = true;
        }
    }

    async ngOnInit() {
        if (this.data) {
            this.transferFundData = this.data;
            await this.resolveCreatorPartnerApprove();
        } else {
            const id = Number(this.route.snapshot.paramMap.get('id'));
            if (!id) {
                console.error('No ID in route');
                this.snackbarService.errorSnackBar('No Id');
                return;
            }

            this.transferFundData = await this.requestService.GetTransferFundbyId(id);
            this.isTransferFundsLoading.set(false);

            await this.resolveCreatorPartnerApprove();
        }

        [this.actionstepBaseUrl, this.actionstepOverviewBaseUrl, this.bankAccounts] = await Promise.all([
            this.requestService.getActionstepUrl(),
            this.requestService.getActionStepOverviewUrl(),
            this.bankAccountService.getAllBankAccounts(),
        ]);
        this.filterBankAccounts();

        this.transferFundRequestForm.get('clientBankAccount')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((account: BankAccount | null) => {
                this.selectedBankAccountId = account?.accountId ?? '';
                if (this.matterNo) {
                    this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
                }
            });

        this.matterNo = this.transferFundData?.matterNo?.toString() || '';
        this.initializeStageFlags();
        this.showFundsChecked();
        this.patchFormValues();
        this.configureFormControls();
        this.setupTransactionReferenceGeneration();

        if (this.transferFundData?.invoice) {
            this.invoiceValues = this.parseInvoiceJson(this.transferFundData.invoice);
        }
        if (this.transferFundData?.matterNo) {
            this.loadBillsByMatter(this.transferFundData.matterNo.toString());
        }
        await this.loadInvoiceTotals();

        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        await this.fetchRpaStatus();
        this.loadDepositAccounts();
        window.addEventListener('storage', this.boundStorageHandler);
    }

    private async resolveCreatorPartnerApprove(): Promise<void> {
        if (this.transferFundData.createdByRole) {
            const res: UserRole = await this.manageUserRoleService
                .getUserPermissionsByRole(this.transferFundData.createdByRole!.toString());
            this.transferFundData.isCreatorPartnerApprove = res.partnerApprove;
            if (res.partnerApprove && this.transferFundData.previousStages) {
                this.transferFundData.previousStages = this.transferFundData.previousStages.filter(s => s.stageOrder !== 2);
            }
        } else {
            this.transferFundData.isCreatorPartnerApprove = false;
        }
    }

    private initializeStageFlags(): void {
        const stageId = this.transferFundData?.currentStage?.stageOrder;
        this.accountsStage = stageId === STAGE_ACCOUNTS;
        this.canEditFields = stageId === STAGE_PARTNER;
        this.showAccountFields = stageId === STAGE_ACCOUNTS ;
        this.isAmountReleased = stageId === STAGE_ACCOUNTS;
        this.isAmountReleasedDisabled = stageId === STAGE_COMPLETED;
        this.disableButtons = stageId === STAGE_COMPLETED;
        this.approveBtn = this.transferFundData?.nextStage?.stageName || '';
        this.previousStages = this.transferFundData?.previousStages?.filter(x => x.stageOrder !== 1) ?? [];
        this.showReturnReason = !!this.transferFundData?.returnReason;
    }

    private patchFormValues(): void {
        this.transferFundRequestForm.patchValue({
            returnReason: this.transferFundData.returnReason,
            matterNumber: this.transferFundData.matterNo,
            matter: this.transferFundData.matter,
            matterName: this.transferFundData.matter,
            client: this.transferFundData.client,
            reason: this.transferFundData.reason,
            invoice: this.transferFundData.invoice,
            amount: this.transferFundData.amount,
            dateRequested: this.transferFundData.dateRequested,
            dateAuthorized: this.transferFundData.dateAuthorised,
            paymentMethod: this.transferFundData.paymentMethod,
            depositAccount: this.transferFundData.depositAccount,
            transactionReference: this.transferFundData.transactionReference,
            fundsCheckedByPartner: this.transferFundData.fundsCheckedByPartner ?? false,
            isCheckedLedger: this.transferFundData.isLedgerChecked ?? false,
            clientBankAccount: this.resolveBankAccount(this.transferFundData.clientBankAccountId) ?? null,
            dateTransaction: this.transferFundData.dateTransaction ? new Date(this.transferFundData.dateTransaction) : null,
            partnerAuthorising: this.transferFundData.partnerAuthorisingName,
        });

        if (this.transferFundData.dateRequested) {
            const d = new Date(this.transferFundData.dateRequested);
            this.transferFundRequestForm.get('requestedTime')?.setValue(this.toTimeString(d));
        }

        if (this.transferFundData?.currentStage?.stageOrder === STAGE_ACCOUNTS) {
            const now = new Date();

            this.transferFundRequestForm.get('dateTransaction')?.setValue(now);
            this.transferFundRequestForm.get('transactionTime')?.setValue(this.toTimeString(now));
        }
    }

    /** Fills the Transaction Reference (TFR plus the day) from the Transaction Date, for the Accounts user, who can still change it. */
    private setupTransactionReferenceGeneration(): void {
        // Only the Accounts user fills in the reference; at other stages the saved value is shown as it is.
        if (!this.accountsStage) return;

        const dateControl = this.transferFundRequestForm.get('dateTransaction');
        const refresh = (): void => {
            this.lastGeneratedTransactionReference = applyGeneratedTransactionReference(
                this.transferFundRequestForm.get('transactionReference'),
                buildTransferTransactionReference(dateControl?.value),
                this.lastGeneratedTransactionReference);
        };

        dateControl?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(refresh);

        refresh();
    }

    private configureFormControls(): void {
        const enableIf = (ctrl: string, condition: boolean) =>
            condition
                ? this.transferFundRequestForm.get(ctrl)?.enable()
                : this.transferFundRequestForm.get(ctrl)?.disable();

        enableIf('reason', this.canEditFields);
        enableIf('clientBankAccount', this.canEditFields || this.accountsStage);;
        enableIf('fundsCheckedByPartner', this.canEditFields);

        // The partner can change the reason, so it cannot be approved empty (as on the create and edit screens).
        const reasonCtrl = this.transferFundRequestForm.get('reason');
        if (this.canEditFields) {
            reasonCtrl?.setValidators([Validators.required]);
        } else {
            reasonCtrl?.clearValidators();
        }
        reasonCtrl?.updateValueAndValidity();

        const depositCtrl = this.transferFundRequestForm.get('depositAccount');
        const transRefCtrl = this.transferFundRequestForm.get('transactionReference');
        enableIf('isCheckedLedger', !this.isAmountReleasedDisabled);

        if (this.accountsStage) {
            depositCtrl?.setValidators([Validators.required]);
            transRefCtrl?.setValidators([Validators.required]);
        } else {
            depositCtrl?.clearValidators();
            transRefCtrl?.clearValidators();
        }
        depositCtrl?.updateValueAndValidity();
        transRefCtrl?.updateValueAndValidity();

        if (!this.isDateTransactionVisible) {
            this.transferFundRequestForm.get('dateTransaction')?.clearValidators();
            this.transferFundRequestForm.get('dateTransaction')?.updateValueAndValidity();
        }

        const isCheckedLedgerControl = this.transferFundRequestForm.get('isCheckedLedger');
        if (this.isAmountReleased && !this.isAmountReleasedDisabled) {
            isCheckedLedgerControl?.setValidators([Validators.requiredTrue]);
        } else {
            isCheckedLedgerControl?.clearValidators();
        }
        isCheckedLedgerControl?.updateValueAndValidity();

        if (this.transferFundData?.dateRequested) {
            this.transferFundRequestForm.get('dateRequested')?.disable();
            this.transferFundRequestForm.get('requestedTime')?.disable();
        }

    }

    private async fetchRpaStatus(): Promise<void> {
        const audits = await this.auditService.getUserAuditByRequest(this.transferFundData.id, RequestTypeOrder[RequestType.TransferFund]);
        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        this.isLastAuditRpaSuccessful = latestAudit?.rpaStatus === RpaStatus.Successful;
    }

    onUrlCopied() {
        this.snackbarService.successSnackBar("URL copied to clipboard");
    }

    loadDepositAccounts(): void {
        this.depositAccountService.getDepositAccounts(6, 366)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (accounts: DepositAccount[]) => {
                    this.depositAccounts = accounts;
                }
            });
    }

    ngAfterViewInit(): void {
        if (this.isDialogOpen()) {
            setTimeout(() => {
                if (this.isDialogOpen()) {
                    const contentEl = this.dialogContent.nativeElement as HTMLElement;
                    const currentHeight = contentEl.offsetHeight;
                    contentEl.style.height = `${currentHeight}px`;
                    contentEl.style.overflowY = 'auto';
                }
            }, 50);
        }
    }

    get getRequestId(): number | undefined {
        return this.transferFundData?.id;
    }

    private parseInvoiceJson(invoiceString: string): { [key: string]: number } {
        try {
            const parsed = JSON.parse(invoiceString);
            const result: { [key: string]: number } = {};

            Object.entries(parsed).forEach(([key, value]) => {
                const match = key.match(/Invoice #(\d+)/);
                if (match) {
                    result[match[1]] = parseFloat(value as string);
                }
            });

            return result;
        } catch (e) {
            console.error('Invalid invoice JSON:', e);
            return {};
        }
    }

    loadBillsByMatter(matterNo: string): void {
        this.billService.getBillsByMatter(matterNo)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    this.bills = data;
                    this.noBillsFoundMessage = null;

                    this.currencySymbol = data.length > 0
                        ? new Intl.NumberFormat('en', {
                            style: 'currency',
                            currency: data[0].currency
                        })
                            .formatToParts(0)
                            .find(part => part.type === 'currency')?.value || data[0].currency
                        : '';

                    this.bills.forEach(bill => {
                        const controlName = this.getControlName(bill);
                        const maxBalance = parseFloat(bill.totalDue);
                        const control = this.formBuilder.control(
                            { value: 0, disabled: !this.canEditFields },
                            [Validators.max(maxBalance)]
                        );

                        this.transferFundRequestForm.addControl(controlName, control);

                        const savedAmount = this.invoiceValues[bill.ourReference];
                        if (savedAmount !== undefined) {
                            control.setValue(savedAmount, { emitEvent: false });
                        }

                        control.valueChanges
                            .pipe(takeUntil(this.destroy$))
                            .subscribe(() => {
                                this.updateTotalAmount();
                                this.updateAutoReason();
                            });
                    });

                    this.updateTotalAmount();
                    this.restoreReasonMode();
                }
            });
    }

    onReasonInput(): void {
        this.reasonEditedByUser = true;
    }

    private buildAutoReason(): string {
        return buildTransferReason(this.bills
            .filter(bill => parseFloat(this.transferFundRequestForm.get(this.getControlName(bill))?.value) > 0)
            .map(bill => bill.ourReference));
    }

    private updateAutoReason(): void {
        if (!this.canEditFields || this.reasonEditedByUser) return;

        this.transferFundRequestForm.get('reason')?.setValue(this.buildAutoReason(), { emitEvent: false });
    }

    private restoreReasonMode(): void {
        if (!this.canEditFields) return;

        const savedReason = this.transferFundRequestForm.get('reason')?.value ?? '';
        this.reasonEditedByUser = savedReason !== '' && savedReason !== this.buildAutoReason();
        this.updateAutoReason();
    }

    async loadInvoiceTotals() {
        const response = await this.requestService.getTransferFundsList();

        this.invoiceMap = {};

        response.forEach((item: any) => {

            if (!item.invoice) return;

            const invoiceObj = JSON.parse(item.invoice);

            Object.keys(invoiceObj).forEach((invoiceKey: string) => {

                const balanceText = invoiceKey.match(/Balance Due: .*?([\d.,]+)/);
                const balance = balanceText ? parseFloat(balanceText[1]) : 0;

                const invoiceNumberMatch = invoiceKey.match(/Invoice #(\d+)/);
                const invoiceNumber = invoiceNumberMatch
                    ? invoiceNumberMatch[1]
                    : invoiceKey;

                const invoiceAmount = parseFloat(invoiceObj[invoiceKey]) || 0;

                if (!this.invoiceMap[invoiceNumber]) {
                    this.invoiceMap[invoiceNumber] = {
                        totalAmount: 0,
                        balance
                    };
                }

                this.invoiceMap[invoiceNumber].totalAmount += invoiceAmount;
            });
        });
    }

    updateTotalAmount() {
        let total = 0;

        this.bills.forEach(bill => {
            const control = this.transferFundRequestForm.get(this.getControlName(bill));
            if (control) {
                total += parseFloat(control.value) || 0;
            }
        });

        total = Math.round(total * 100) / 100;
        this.transferFundRequestForm.get('amount')?.setValue(total, { emitEvent: false });
    }
    getControlName(bill: Bill) {
        return `invoice${bill.id}`
    }

    private resolveBankAccount(accountId?: string): BankAccount | undefined {
        return this.bankAccounts.find(a => a.accountId === accountId);
    }

    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    validateInput(event: KeyboardEvent): void {
        const allowedKeys = /[0-9.,]/;
        const key = event.key;
        if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(key)) {
            return;
        }
        if (!allowedKeys.test(key)) {
            event.preventDefault();
        }
    }

    get isDateTransactionVisible(): boolean {
        return (this.transferFundData?.currentStage?.stageOrder ?? 0) >= STAGE_ACCOUNTS;
    }

    showFundsChecked() {
        const stageId = this.transferFundData?.currentStage?.stageOrder;

        this.showFundsCheckedByPartner = stageId === 3;
        this.FundsCheckedPartnerText = stageId === 4 || stageId === 5;

        if (stageId === 3) {
            this.transferFundRequestForm.get('fundsCheckedByPartner')?.setValidators([Validators.requiredTrue]);
        }

        if (stageId === 4) {
            this.transferFundRequestForm.get('fundsCheckedByPartner')?.setValidators([Validators.requiredTrue]);
        }

        this.transferFundRequestForm.get('fundsCheckedByPartner')?.updateValueAndValidity();
    }

    get hasAttachments(): boolean {
        return this.transferFundData?.documentAvailable ?? false;
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: Event): void {
        this.unlockPopup();
    }

    unlockPopup() {
        this.requestService.requestLockorUnLock(this.transferFundData.id, RequestType.TransferFund).then((res) => {
            if (res) {
                this.snackbarService.successSnackBar("The request has been unlocked.");
            }
        })
    }

    closeDialog() {
        if (this.dialogRef) {
            this.dialogRef.close(true);
        }

        if (!this.isDialogOpen()) {
            window.close();
        }
    }

    isReturnDisabled(stageId: number): boolean {
        if (!this.isLastAuditRpaSuccessful) {
            return false;
        }
        const currentStageId = this.transferFundData?.currentStage?.stageOrder;
        if (currentStageId === STAGE_ACCOUNTS) {
            return false;
        }
        return stageId !== STAGE_ACCOUNTS;
    }

    async returnRequest(stageId: number, stageName: string) {
        const audits = await this.auditService.getUserAuditByRequest(this.transferFundData.id, RequestTypeOrder[RequestType.TransferFund]);
        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        const rpaStatus = latestAudit?.rpaStatus === RpaStatus.Successful;
        const dialogRef = this.dialog.open(ReturnReasonDialogComponent, {
            width: '600px',
            data: {
                stageName,
                matterNo: this.transferFundData?.matterNo,
                currentStageId: this.transferFundData?.currentStage?.stageOrder,
                rpaStatus: rpaStatus,
                selectedBankAccountId: this.selectedBankAccountId,
            },
            disableClose: true
        });

        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
            if (result) {
                this.requestService.returnTransferRequest(this.transferFundData.id, stageId,
                    result.reason,
                    result.isRPAReversalRequested)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.closeDialog();
                        }
                    })
            }
        });
    }

    async delete() {
        const audits = await this.auditService.getUserAuditByRequest(this.transferFundData.id, RequestTypeOrder[RequestType.TransferFund]);
        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        const rpaStatus = latestAudit?.rpaStatus === RpaStatus.Successful;
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '600px',
            data: {
                title: 'Are you sure?',
                message: 'The data associated with the request will be deleted.',
                dialogType: 'warning',
                buttonType: 'ConfirmCancel',
                confirmText: 'Confirm',
                cancelText: 'Cancel',
                matterNo: this.transferFundData?.matterNo,
                currentStageId: this.transferFundData?.currentStage?.stageOrder,
                rpaStatus: rpaStatus,
                selectedBankAccountId: this.selectedBankAccountId,
            }
        });

        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((res) => {
            if (res) {
                this.requestService.deleteTransferFund(this.transferFundData.id!)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.closeDialog();
                        }
                    })
                    .catch((error) => {
                        this.snackbarService.errorSnackBar("Failed to delete transfer request. Please try again.");
                    });
            }

        });
    }

    approveRequest() {

        if (this.transferFundRequestForm.invalid) {
            this.transferFundRequestForm.markAllAsTouched();
            return;
        }

        let invoices: { [key: string]: string } = {};
        this.bills.forEach(bill => {
            const amountControl = this.transferFundRequestForm.get(`invoice${bill.id}`);
            if (amountControl && amountControl.value > 0) {
                invoices[`Invoice #${bill.ourReference} (Balance Due: ${this.currencySymbol}${bill.totalDue})`] = amountControl.value;
            }
        });

        const selectedAccount = this.transferFundRequestForm.get('clientBankAccount')?.value as BankAccount | null;

        const approvalDetails: ApprovalDetails = {
            ...this.transferFundData,
            dateTransaction: (() => {
                const datePart = this.transferFundRequestForm.get('dateTransaction')?.value;
                if (!datePart) return undefined;

                const d = datePart instanceof Date ? datePart : new Date(datePart);
                const [hours, minutes] = (
                    this.transferFundRequestForm.get('transactionTime')?.value ?? '00:00'
                ).split(':').map(Number);

                return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes, 0, 0);
            })(),
            reasonForTransfer: this.transferFundRequestForm.get('reason')?.value || this.transferFundData.reason || "",
            invoice: JSON.stringify(invoices),
            amount: this.transferFundRequestForm.get('amount')?.value,
            paymentMethod: this.transferFundRequestForm.get('paymentMethod')?.value,
            depositAccount: this.transferFundRequestForm.get('depositAccount')?.value,
            transactionReference: this.transferFundRequestForm.get('transactionReference')?.value,
            isLedgerChecked: this.transferFundRequestForm.value.isCheckedLedger,
            ledgerCheckedBy: JSON.parse(localStorage.getItem("UserDetails")!).fullName,
            matter: this.transferFundRequestForm.get('matter')?.value,
            comments: this.transferFundRequestForm.value.comments,
            requestDate: this.transferFundRequestForm.get('requestDate')?.value,
            fundsCheckedByPartner: this.transferFundRequestForm.value.fundsCheckedByPartner || this.transferFundData.fundsCheckedByPartner,
            partnerFundsCheckedBy: JSON.parse(localStorage.getItem("UserDetails")!).fullName,
            clientBankAccountName: selectedAccount ? `${selectedAccount.accountId} - ${selectedAccount.description}` : this.transferFundData.clientBankAccountName,
            clientBankAccountId: selectedAccount?.accountId ?? this.transferFundData.clientBankAccountId,
        } as unknown as ApprovalDetails;

        this.requestService.approveRequest(this.transferFundData.id!, RequestType.TransferFund, this.transferFundData.nextStage?.id ?? 0, approvalDetails)
            .then((res: string) => {
                if (res) {
                    this.snackbarService.successSnackBar(`${res}`);
                    this.closeDialog()
                }
            })
            .catch((error) => {
                this.snackbarService.errorSnackBar("Failed to approve transfer request. Please try again.");
            });
    }

    filterBankAccounts() {
        this.filteredBankAccounts = [...this.bankAccounts];
    }

    @HostListener('window:beforeunload')
    onExpandedTabClose() {
        localStorage.setItem('transferExpandedClosed', Date.now().toString());
    }

    openInNewTab() {
        const id = this.transferFundData?.id;
        if (!id) return;
        localStorage.setItem('transferExpandedOpen', Date.now().toString());
        const url = this.router.serializeUrl(
            this.router.createUrlTree([
                '/approvals',
                'transfer-funds-request',
                id
            ])
        );
        window.open(window.location.origin + url, '_blank');
    }

    onStorageEvent(event: StorageEvent) {

        if (event.key === 'transferExpandedClosed') {
            this.closeDialog();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        window.removeEventListener('storage', this.boundStorageHandler);
    }
}
